import { Capacitor, PermissionState } from "@capacitor/core"
import { Geolocation, Position } from '@capacitor/geolocation'
import { Location, FusionError, serializeFusionError } from "@fusion-advantage/backcompat"
import { LocationStatus } from '@fusion-advantage/formrender'
import { err, ok, Result } from "neverthrow"

export type GetLocationResultOk = {
	status: (
		LocationStatus.Success
	)
	error?: never
	location: Location
}

export type GetLocationResultErr = {
	status: (
		LocationStatus.NoBrowserSupport|
		LocationStatus.Prompt|
		LocationStatus.Denied
	),
	error?: never
} | {
	status: (
		LocationStatus.ServicesDisabled|
		LocationStatus.Unknown
	)
	error: FusionError
}

// a single outcome delivered by watchLocation: a fresh fix or a transient error
export type LocationFix = Result<GetLocationResultOk, GetLocationResultErr>

// browser (Web Geolocation) acquisition options, shared by the one-shot and watch
// paths. A desktop browser has no GPS chip — location comes from the OS/network
// provider (Wi-Fi/IP) — so enableHighAccuracy is left false everywhere (it only
// adds latency and timeout risk without improving a network-derived fix).
// The timeout stops the spinner hanging forever (originally seen in Edge); the
// maximumAge lets a recent cached fix satisfy a request, which keeps the value
// consistent and avoids needless re-queries.
const BROWSER_LOCATION_TIMEOUT_MS = 5 * 1000
const BROWSER_LOCATION_MAX_AGE_MS = 60 * 1000

// map a Web Geolocation error to a GetLocationResultErr using the structured,
// locale-independent error.code (message text is localized and unreliable).
const browserLocationError = (ctx: string, ex: GeolocationPositionError): GetLocationResultErr => {
	if(ex.code === ex.PERMISSION_DENIED) {
		return { status: LocationStatus.Denied }
	}
	// POSITION_UNAVAILABLE / TIMEOUT — no dedicated enum member, treat as Unknown
	return {
		status: LocationStatus.Unknown,
		error: serializeFusionError(ctx, ex),
	}
}

// browser permission pre-check mirroring native's synchronous checkPermissions().
// Returns an up-front Err for 'denied'/'prompt' so callers (and the location
// middleware's modal) behave the same as on native; returns null when we should
// proceed to acquisition ('granted', or when the Permissions API is unavailable
// so denial can only surface later via the acquisition error callback).
const browserCheckPermission = async (): Promise<GetLocationResultErr|null> => {
	if(!navigator.permissions?.query) {
		return null
	}
	try {
		const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
		switch(status.state) {
			case 'denied':
				return { status: LocationStatus.Denied }
			case 'prompt':
				return { status: LocationStatus.Prompt }
			default:
				return null
		}
	} catch {
		// Permissions API present but query rejected — fall through to acquisition
		return null
	}
}

// handle to a running location watch; call clear() to stop it
export interface LocationWatch {
	clear: () => void
}

export const getLocation = async (opts?: { enableHighAccuracy?: boolean }): Promise<Result<GetLocationResultOk, GetLocationResultErr>> => {
	// default to a fast, wifi/cell-assisted fix over a slow high-power GNSS one:
	// getting *a* fix matters more than pinpoint accuracy.
	const enableHighAccuracy = opts?.enableHighAccuracy ?? false
	if(!Capacitor.isNativePlatform()) {
		const permErr = await browserCheckPermission()
		if(permErr) {
			return err(permErr)
		}
		return browserGetLocation(enableHighAccuracy)
	}
	let permissionState: PermissionState
	try {
		// according to hint, will throw if services are disabled
		const permissionStatus = await Geolocation.checkPermissions()
		permissionState = permissionStatus.location
	} catch (ex) {
		return err({
			status: LocationStatus.ServicesDisabled,
			error: serializeFusionError('getLocation (native)', ex),
		})
	}
	switch(permissionState) {
		case 'granted':
			break
		case 'denied':
			return err({
				status: LocationStatus.Denied,
			})
		case 'prompt':
		case 'prompt-with-rationale':
			return err({
				status: LocationStatus.Prompt,
			})
		default:
			return err({
				status: LocationStatus.Unknown,
				error: serializeFusionError('getLocation (native)', `unhandled permission state ${permissionState}`)
			})
	}
	let pos: Position
	try {
		pos = await Geolocation.getCurrentPosition({enableHighAccuracy})
	} catch(ex) {
		return err({
			status: LocationStatus.Unknown,
			error: serializeFusionError('getLocation (native)', ex)
		})
	}
	const { latitude, longitude, accuracy } = pos.coords
	return ok({
		status: LocationStatus.Success,
		location: {
			latitude,
			longitude,
			accuracy,
		},
	})
}

// Start a continuous location watch. Resolves once the watch is established
// (Ok, with a handle to stop it) or with the up-front permission/service error
// that prevents it from starting (Err). Every subsequent fix — or transient
// error — is delivered to `onFix` for as long as the watch runs.
//
// enableHighAccuracy is intentionally false: we want the fast, wifi/cell-assisted
// fix that almost never fails over a slow, high-power GNSS fix that can time out.
// The watch keeps the receiver warm and refines accuracy opportunistically.
export const watchLocation = async (
	onFix: (fix: LocationFix) => void,
): Promise<Result<LocationWatch, GetLocationResultErr>> => {
	if(!Capacitor.isNativePlatform()) {
		const permErr = await browserCheckPermission()
		if(permErr) {
			return err(permErr)
		}
		return browserWatchLocation(onFix)
	}
	let permissionState: PermissionState
	try {
		// according to hint, will throw if services are disabled
		const permissionStatus = await Geolocation.checkPermissions()
		permissionState = permissionStatus.location
	} catch (ex) {
		return err({
			status: LocationStatus.ServicesDisabled,
			error: serializeFusionError('watchLocation (native)', ex),
		})
	}
	switch(permissionState) {
		case 'granted':
			break
		case 'denied':
			return err({
				status: LocationStatus.Denied,
			})
		case 'prompt':
		case 'prompt-with-rationale':
			return err({
				status: LocationStatus.Prompt,
			})
		default:
			return err({
				status: LocationStatus.Unknown,
				error: serializeFusionError('watchLocation (native)', `unhandled permission state ${permissionState}`)
			})
	}
	let watchId: string
	try {
		watchId = await Geolocation.watchPosition({ enableHighAccuracy: false }, (pos, ex) => {
			if(ex || !pos) {
				onFix(err({
					status: LocationStatus.Unknown,
					error: serializeFusionError('watchLocation (native)', ex ?? 'null position'),
				}))
				return
			}
			const { latitude, longitude, accuracy } = pos.coords
			onFix(ok({
				status: LocationStatus.Success,
				location: { latitude, longitude, accuracy },
			}))
		})
	} catch(ex) {
		return err({
			status: LocationStatus.Unknown,
			error: serializeFusionError('watchLocation (native)', ex),
		})
	}
	return ok({
		clear: () => { void Geolocation.clearWatch({ id: watchId }) },
	})
}

export const requestLocationPermissions = async (): Promise<Result<void, GetLocationResultErr>> => {
	if(!Capacitor.isNativePlatform()) {
		// there's no separate "request permission" web API — calling
		// getCurrentPosition is what actually shows the browser's location prompt.
		const r = await browserGetLocation()
		if(r.isOk()) {
			return ok()
		}
		return err(r.error)
	}
	// this message now displayed by LocationModal
	// alert(`As a condition of your engagement with Fusion Advantage, you are required to allow us access to your location while starting or performing a patient encounter, or while making changes to encounter data. This data is utilized for patient safety and fraud prevention purposes. Tap 'Allow While Using App' on the following dialog to continue using the application. If you have any questions about this requirement, please contact clinician field support.`)
	let permissionState: PermissionState
	try {
		// according to hint, will throw if services are disabled
		const permissionStatus = await Geolocation.requestPermissions()
		permissionState = permissionStatus.location
	} catch(ex) {
		// however, by this time we should have already checked the permissions
		// so this would seem to be an unexpected state
		return err({
			status: LocationStatus.ServicesDisabled,
			error: serializeFusionError('requestLocationPermissions', ex)
		})
	}
	switch(permissionState) {
		case 'granted':
			return ok()
		case 'denied':
			return err({
				status: LocationStatus.Denied,
			})
		default:
			return err({
				status: LocationStatus.Unknown,
				error: serializeFusionError('requestLocationPermissions', `unhandled permission state ${permissionState}`)
			})
	}
}

// 20250908
// Capacitor plugin site doesn't even mention supporting the web anymore,
// but that's fine because it didn't work in the first place.
// we'll keep our custom code included, then.
const browserGetLocation = async (enableHighAccuracy = false): Promise<Result<GetLocationResultOk, GetLocationResultErr>> => {
	// https://developer.mozilla.org/en-US/docs/Web/API/Geolocation_API/Using_the_Geolocation_API
	if(!('geolocation' in navigator)) {
		return err({
			status: LocationStatus.NoBrowserSupport,
		})
	}
	return new Promise<Result<GetLocationResultOk, GetLocationResultErr>>((resolve) => {
		try {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude, accuracy } = position.coords
					resolve(ok({
						status: LocationStatus.Success,
						location: {
							latitude,
							longitude,
							accuracy,
						},
					}))
				},
				(ex) => {
					resolve(err(browserLocationError('browserGetLocation', ex)))
				},
				{
					// the timeout stops the spinner hanging forever (originally
					// seen for Dr. Dambro in Edge, where it would spin forever and
					// then sometimes crash the form). enableHighAccuracy is a no-op
					// on a browser with no GPS chip, so it stays false here — the
					// location on the browser is of far less value than that of the
					// clinicians in the field on iPads... GO TEAM 🍻
					enableHighAccuracy,
					timeout: BROWSER_LOCATION_TIMEOUT_MS,
					maximumAge: BROWSER_LOCATION_MAX_AGE_MS,
				},
			)
		} catch(ex) {
			resolve(err({
				status: LocationStatus.Unknown,
				error: serializeFusionError('browserGetLocation', ex),
			}))
		}
	})
}

const browserWatchLocation = (
	onFix: (fix: LocationFix) => void,
): Result<LocationWatch, GetLocationResultErr> => {
	if(!('geolocation' in navigator)) {
		return err({
			status: LocationStatus.NoBrowserSupport,
		})
	}
	// callers pre-check permission via browserCheckPermission() where the
	// Permissions API is available; where it isn't, denial still only surfaces via
	// the error callback (delivered through onFix) rather than as an up-front Err.
	const watchId = navigator.geolocation.watchPosition(
		(position) => {
			const { latitude, longitude, accuracy } = position.coords
			onFix(ok({
				status: LocationStatus.Success,
				location: {
					latitude,
					longitude,
					accuracy,
				},
			}))
		},
		(ex) => {
			onFix(err(browserLocationError('browserWatchLocation', ex)))
		},
		{
			// same anti-hang treatment as browserGetLocation: enableHighAccuracy is
			// a no-op without a GPS chip; the timeout prevents an indefinite hang;
			// maximumAge lets a recent cached fix satisfy the watch, keeping the
			// value consistent on an essentially-static desktop position.
			enableHighAccuracy: false,
			timeout: BROWSER_LOCATION_TIMEOUT_MS,
			maximumAge: BROWSER_LOCATION_MAX_AGE_MS,
		},
	)
	return ok({
		clear: () => navigator.geolocation.clearWatch(watchId),
	})
}
